import styles from './AssistedModeJoinDialog.module.css';

import { Field, FieldArray, Formik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';

import { bngYup } from 'components/bng/form/yup/BngYup';
import Dialog from 'components/ui/Dialog';
import useTranslation from 'components/hooks/useTranslation';
import UiBlocker from 'components/bng/ui/UiBlocker';
import { DefaultDialogActions } from 'components/ui/FormUtils';
import BngSelectSearch from 'components/bng/form/BngSelectSearch';
import BngField from 'components/bng/form/BngField';
import BngForm from 'components/bng/form/BngForm';
import Icon from 'components/ui/common/Icon';
import Api from 'components/Api';
import BngIconButton from 'components/bng/ui/BngIconButton';
import {
  buildComparisonOptions,
  OperatorToggle,
} from 'components/bng/pages/admin/structures/bimQuery/dialogs/AssistedModeFilterDialog';
import UUID from 'bng/utils/UUID';
import { JOIN_ICONS } from 'components/ui/common/DatabaseERModel';

const buildSchema = bngYup((yup) => {
  return yup.object({
    joinType: yup.string().required().default('INNER_JOIN'),
    joinConditions: yup.array(
      yup.object({
        id: yup.string().nullable().default(''),
        sourceField: yup.string().required().default(''),
        operator: yup.string().required().default(''),
        targetField: yup.string().required().default(''),
        targetNodeId: yup.string().nullable().default(''),
        logicalOperator: yup.string().nullable().default('AND'),
      })
    ),
  });
});

export const buildJoinTypeOptions = (t) => [
  {
    value: 'INNER_JOIN',
    label: t('inner.join'),
    explanation: t('inner.join.explanatory.text'),
  },
  {
    value: 'LEFT_JOIN',
    label: t('left.join'),
    explanation: t('left.join.explanatory.text'),
  },
  {
    value: 'RIGHT_JOIN',
    label: t('right.join'),
    explanation: t('right.join.explanatory.text'),
  },
  {
    value: 'FULL_OUTER_JOIN',
    label: t('full.outer.join'),
    explanation: t('full.outer.join.explanatory.text'),
  },
];

const createFixedValueOption = (searchTerm, t) => ({
  value: `__FIXED_VALUE__${searchTerm}`,
  label: `${t('FIXED')}: "${searchTerm}"`,
  isFixedValue: true,
  fixedValue: searchTerm,
});

const buildDisplayPrefix = (tableInfo) => {
  return tableInfo?.alias || tableInfo?.table || '';
};

export default function AssistedModeJoinDialog({
  closeModal,
  nodeId,
  nodes = [],
  joins = [],
  onChange = _.noop,
  onDeleteTable = _.noop,
  highlightedField = null,
}) {
  const { t } = useTranslation();
  const [targetFieldSearchTerms, setTargetFieldSearchTerms] = useState({});

  const currentNode = nodes.find((node) => node.id === nodeId);
  const currentNodeFields = currentNode?.data?.tableInfo?.fields;
  const tableInfo = currentNode.data?.tableInfo;
  const tableName = tableInfo.table;
  const displayName = tableInfo?.alias ? `${tableInfo.alias} (${tableInfo.table})` : tableInfo?.table;

  const getDefaultCondition = (fieldName) => {
    const field = currentNodeFields?.find((f) => f.name === fieldName);
    return field?.type === 'BOOL' ? '=' : '';
  };

  const existingJoins = joins.filter((join) => {
    if (join.fixedValue !== undefined && join.fixedValue !== null) {
      return join.sourceNodeId === nodeId;
    }

    const sourceNode = nodes.find((node) => node.id === join.sourceNodeId);
    const targetNode = nodes.find((node) => node.id === join.targetNodeId);

    if (!sourceNode || !targetNode) return false;

    const sourceOrder = sourceNode.data?.tableInfo?.tableOrder || 0;
    const targetOrder = targetNode.data?.tableInfo?.tableOrder || 0;

    if (sourceOrder > targetOrder) {
      return join.sourceNodeId === nodeId;
    } else {
      return join.targetNodeId === nodeId;
    }
  });

  const currentTableOrder = currentNode?.data?.tableInfo?.tableOrder || 0;
  const otherNodesFields = nodes
    .filter((node) => {
      if (node.id === nodeId) return false;
      const nodeTableOrder = node.data?.tableInfo?.tableOrder || 0;
      return nodeTableOrder < currentTableOrder;
    })
    .reduce((acc, node) => {
      const nodeFields = node?.data?.tableInfo?.fields || [];
      const tableInfo = node.data?.tableInfo;
      return [
        ...acc,
        ...nodeFields.map((field) => ({
          ...field,
          displayName: `${buildDisplayPrefix(tableInfo)}.${field.name}`,
          nodeId: node.id,
          nodeName: tableInfo.table,
          nodeAlias: tableInfo?.alias,
        })),
      ];
    }, []);

  const initialConditions = useMemo(
    () =>
      existingJoins.length > 0
        ? existingJoins.map((join, index) => {
            if (join.fixedValue !== undefined && join.fixedValue !== null) {
              return {
                id: join.id,
                sourceField: join.sourceField || '',
                operator: join.operator || getDefaultCondition(join.sourceField),
                targetField: join.fixedValue,
                targetNodeId: '',
                joinType: join.joinType || 'INNER_JOIN',
                logicalOperator: index === 0 ? null : join.logicalOperator || 'AND',
              };
            }

            const sourceNode = nodes.find((node) => node.id === join.sourceNodeId);
            const targetNode = nodes.find((node) => node.id === join.targetNodeId);
            const sourceOrder = sourceNode?.data?.tableInfo?.tableOrder || 0;
            const targetOrder = targetNode?.data?.tableInfo?.tableOrder || 0;

            let sourceField, targetField, targetNodeId;

            if (sourceOrder > targetOrder) {
              const targetTableInfo = targetNode?.data?.tableInfo;
              sourceField = join.sourceField;
              targetField = join.targetField.includes('.')
                ? join.targetField
                : `${buildDisplayPrefix(targetTableInfo)}.${join.targetField}`;
              targetNodeId = join.targetNodeId;
            } else {
              const sourceTableInfo = sourceNode?.data?.tableInfo;
              sourceField = join.targetField;
              targetField = join.sourceField.includes('.')
                ? join.sourceField
                : `${buildDisplayPrefix(sourceTableInfo)}.${join.sourceField}`;
              targetNodeId = join.sourceNodeId;
            }

            return {
              id: join.id,
              sourceField: sourceField || '',
              operator: join.operator || getDefaultCondition(sourceField),
              targetField: targetField,
              targetNodeId: targetNodeId || '',
              joinType: join.joinType || 'INNER_JOIN',
              logicalOperator: index === 0 ? null : join.logicalOperator || 'AND',
            };
          })
        : [],
    [existingJoins, nodes, getDefaultCondition]
  );

  const joinTypeOpts = useMemo(() => buildJoinTypeOptions(t), []);

  const getJoinExplanation = (joinType) => {
    const option = joinTypeOpts.find((opt) => opt.value === joinType);
    return option ? option.explanation : '';
  };

  const getTargetFieldOptions = useCallback(
    (conditionIndex, currentValue) => {
      const searchTerm = targetFieldSearchTerms[conditionIndex] || '';
      const baseOptions = otherNodesFields.map((field) => ({
        value: field.displayName,
        label: field.displayName,
      }));

      let options = [...baseOptions];

      if (searchTerm && searchTerm.trim()) {
        options = [createFixedValueOption(searchTerm.trim(), t), ...baseOptions];
      }

      if (currentValue?.trim()) {
        const isValidFieldOption = otherNodesFields.some((field) => field.displayName === currentValue);
        const isAlreadyFixedValueOption = options.some((opt) => opt.isFixedValue && opt.fixedValue === currentValue);
        const isAlreadyInBaseOptions = baseOptions.some((opt) => opt.value === currentValue);

        if (!isValidFieldOption && !isAlreadyFixedValueOption && !isAlreadyInBaseOptions) {
          const fixedValueOption = {
            value: currentValue,
            label: `${t('FIXED')}: "${currentValue}"`,
            isFixedValue: true,
            fixedValue: currentValue,
          };
          options = [fixedValueOption, ...options];
        }
      }

      return options;
    },
    [otherNodesFields, targetFieldSearchTerms, t]
  );

  const handleSubmit = useCallback(
    (dialogValues) => {
      const validConditions = dialogValues.joinConditions.filter(
        (condition) => condition.sourceField && condition.targetField
      );

      const joinConditions = validConditions.filter((condition) => {
        const targetFieldInfo = otherNodesFields.find((field) => field.displayName === condition.targetField);
        return targetFieldInfo;
      });

      const fixedValueConditions = validConditions.filter((condition) => {
        const targetFieldInfo = otherNodesFields.find((field) => field.displayName === condition.targetField);
        return !targetFieldInfo;
      });

      const existingJoinConditions = joinConditions.filter((condition) => condition.id);
      const newJoinConditions = joinConditions.filter((condition) => !condition.id);

      const joinsToKeep = existingJoinConditions.map((condition) => condition.id);

      const belongsToCurrentNode = (join) => {
        if (join.fixedValue != null) {
          return join.sourceNodeId === nodeId;
        }

        const sourceNode = nodes.find((n) => n.id === join.sourceNodeId);
        const targetNode = nodes.find((n) => n.id === join.targetNodeId);
        if (!sourceNode || !targetNode) return false;

        const sourceOrder = sourceNode.data?.tableInfo?.tableOrder || 0;
        const targetOrder = targetNode.data?.tableInfo?.tableOrder || 0;

        if (sourceOrder > targetOrder) {
          return join.sourceNodeId === nodeId;
        } else {
          return join.targetNodeId === nodeId;
        }
      };

      const otherJoins = joins.filter((join) => {
        if (!belongsToCurrentNode(join)) {
          return true;
        }
        if (join.fixedValue != null) {
          return joinsToKeep.includes(join.id);
        }

        return joinsToKeep.includes(join.id);
      });

      const updatedExistingJoins = otherJoins.map((join) => {
        if ((join.sourceNodeId === nodeId || join.targetNodeId === nodeId) && joinsToKeep.includes(join.id)) {
          const condition = existingJoinConditions.find((c) => c.id === join.id);
          if (condition) {
            const targetFieldInfo = otherNodesFields.find((field) => field.displayName === condition.targetField);

            if (targetFieldInfo) {
              const targetFieldName = condition.targetField.includes('.')
                ? condition.targetField.split('.').pop()
                : condition.targetField;

              const sourceNode = nodes.find((node) => node.id === join.sourceNodeId);
              const targetNode = nodes.find((node) => node.id === join.targetNodeId);
              const sourceOrder = sourceNode?.data?.tableInfo?.tableOrder || 0;
              const targetOrder = targetNode?.data?.tableInfo?.tableOrder || 0;

              if (sourceOrder > targetOrder) {
                return {
                  ...join,
                  sourceField: condition.sourceField,
                  targetNodeId: targetFieldInfo.nodeId,
                  targetNodeName: targetFieldInfo.nodeName,
                  targetField: targetFieldName,
                  joinType: dialogValues.joinType,
                  operator: condition.operator,
                  logicalOperator: condition.logicalOperator,
                  tableOrder: sourceOrder,
                  fixedValue: undefined,
                };
              } else {
                return {
                  ...join,
                  sourceField: targetFieldName,
                  sourceNodeId: targetFieldInfo.nodeId,
                  sourceNodeName: targetFieldInfo.nodeName,
                  targetField: condition.sourceField,
                  targetNodeId: nodeId,
                  targetNodeName: tableName,
                  joinType: dialogValues.joinType,
                  operator: condition.operator,
                  logicalOperator: condition.logicalOperator,
                  tableOrder: targetOrder,
                  fixedValue: undefined,
                };
              }
            } else {
              return {
                ...join,
                sourceNodeId: nodeId,
                sourceNodeName: tableName,
                sourceField: condition.sourceField,
                operator: condition.operator,
                logicalOperator: condition.logicalOperator,
                fixedValue: condition.targetField,
                joinType: dialogValues.joinType,
                tableOrder: currentTableOrder,
                targetNodeId: null,
                targetNodeName: null,
                targetField: null,
              };
            }
          }
        }

        return join;
      });

      const newJoins = newJoinConditions.map((condition, index) => {
        const targetFieldInfo = otherNodesFields.find((field) => field.displayName === condition.targetField);
        const targetFieldName = condition.targetField.includes('.')
          ? condition.targetField.split('.').pop()
          : condition.targetField;

        const globalIndex = existingJoinConditions.length + index;

        const targetTableOrder = targetFieldInfo
          ? nodes.find((n) => n.id === targetFieldInfo.nodeId)?.data?.tableInfo?.tableOrder || 0
          : 0;

        if (currentTableOrder > targetTableOrder) {
          return {
            id: UUID.generate(),
            sourceNodeId: nodeId,
            sourceNodeName: tableName,
            sourceField: condition.sourceField,
            targetNodeId: targetFieldInfo?.nodeId,
            targetNodeName: targetFieldInfo?.nodeName || '',
            targetField: targetFieldName,
            joinType: dialogValues.joinType,
            operator: condition.operator,
            ...(globalIndex > 0 ? { logicalOperator: condition.logicalOperator || 'AND' } : {}),
            tableOrder: currentTableOrder,
          };
        } else {
          return {
            id: UUID.generate(),
            sourceNodeId: targetFieldInfo?.nodeId,
            sourceNodeName: targetFieldInfo?.nodeName || '',
            sourceField: targetFieldName,
            targetNodeId: nodeId,
            targetNodeName: tableName,
            targetField: condition.sourceField,
            joinType: dialogValues.joinType,
            operator: condition.operator,
            ...(globalIndex > 0 ? { logicalOperator: condition.logicalOperator || 'AND' } : {}),
            tableOrder: targetTableOrder,
          };
        }
      });

      const existingFixedValueConditions = fixedValueConditions.filter((condition) => condition.id);
      const newFixedValueConditions = fixedValueConditions.filter((condition) => !condition.id);

      const updatedExistingFixedValueJoins = updatedExistingJoins.map((join) => {
        if (join.fixedValue && joinsToKeep.includes(join.id)) {
          const condition = existingFixedValueConditions.find((c) => c.id === join.id);
          if (condition) {
            return {
              ...join,
              sourceField: condition.sourceField,
              operator: condition.operator,
              logicalOperator: condition.logicalOperator,
              fixedValue: condition.targetField,
              joinType: dialogValues.joinType,
            };
          }
        }
        return join;
      });

      const newFixedValueJoins = newFixedValueConditions.map((condition, index) => {
        const globalIndex = existingJoinConditions.length + newJoinConditions.length + index;

        return {
          id: UUID.generate(),
          sourceNodeId: nodeId,
          sourceNodeName: tableName,
          sourceField: condition.sourceField,
          targetNodeId: null,
          targetNodeName: null,
          targetField: null,
          fixedValue: condition.targetField,
          joinType: dialogValues.joinType,
          operator: condition.operator,
          ...(globalIndex > 0 ? { logicalOperator: condition.logicalOperator || 'AND' } : {}),
          tableOrder: currentTableOrder,
        };
      });

      const finalJoins = [...updatedExistingFixedValueJoins, ...newJoins, ...newFixedValueJoins];

      const updatedNodes = nodes.map((node) => {
        if (node.id === nodeId) {
          node = _.cloneDeep(node);
          node.data.tableInfo.joinType = dialogValues.joinType;
        }
        return node;
      });

      onChange({
        nodes: updatedNodes,
        joins: finalJoins,
      });
      closeModal();
    },
    [nodes, joins, nodeId, tableName, otherNodesFields, existingJoins, onChange, closeModal, currentTableOrder]
  );

  const comparisonOptions = buildComparisonOptions(t);
  const hasInitialConditions = initialConditions.length > 0;

  return (
    <Formik
      initialValues={{
        joinType: currentNode?.data?.tableInfo?.joinType || 'INNER_JOIN',
        joinConditions: initialConditions,
      }}
      onSubmit={handleSubmit}
      validationSchema={buildSchema}
    >
      {({ values, submitCount, setFieldValue }) => {
        const shouldHideCloseAndCancel = !hasInitialConditions && values.joinConditions.length === 0;
        return (
          <Dialog
            className={styles.joinDialogContainer}
            title={`${t('bim.query.join')}, ${displayName}`}
            onClose={closeModal}
            newDialogLayout={true}
            hideClose={shouldHideCloseAndCancel}
          >
            <UiBlocker>
              <BngForm>
                <Dialog.Body className={styles.dialogBodyContainer}>
                  <div className={styles.joinTypeWrapper}>
                    <div className={styles.joinTypeTitle}>{t('join.type')}</div>
                    <div className={styles.joinTypeSelectWrapper}>
                      <Field
                        name="joinType"
                        className={styles.joinTypeSelect}
                        component={BngField}
                        inputComponent={BngSelectSearch}
                        withLabel={false}
                        showSearchBar={false}
                        clearable={false}
                        options={joinTypeOpts}
                        required
                      />
                      <Icon
                        icon={
                          <img
                            src={Api.buildUrl(JOIN_ICONS[values.joinType] || JOIN_ICONS.INNER_JOIN)}
                            width={48}
                            height={48}
                            alt="Join type image"
                          />
                        }
                        className={styles.joinTypeIcon}
                      />
                      <div className={styles.joinTypeExplanation}>{getJoinExplanation(values.joinType)}</div>
                    </div>
                  </div>

                  <FieldArray name="joinConditions">
                    {({ push, remove }) => (
                      <div className={styles.joinConditionsWrapper}>
                        <div className={styles.joinConditionHeader}>
                          <div className={styles.joinConditionTitle}>{t('join.condition')}</div>
                          <BngIconButton
                            icon={'add_circle'}
                            onClick={() => {
                              push({
                                id: null,
                                sourceField: '',
                                operator: '=',
                                targetField: '',
                                targetNodeId: '',
                                logicalOperator: values.joinConditions.length > 0 ? 'AND' : null,
                              });
                            }}
                            className={styles.addButton}
                          />
                        </div>

                        {values.joinConditions.length === 0 && (
                          <div className={styles.joinConditionEmptyWrapper}>
                            <div className={styles.joinConditionEmptyTextWrapper}>
                              <div className={styles.joinConditionEmptyText}>
                                {t('click.on.icon')}
                                <Icon icon={'add_circle'} className={styles.addButton} />
                                {t('add.join.condition')}
                              </div>
                              <div>{t('one.condition.at.least.message')}</div>
                            </div>
                          </div>
                        )}

                        {values.joinConditions.map((condition, index) => {
                          const selectedFieldName = values.joinConditions[index].sourceField;
                          const selectedField = currentNodeFields?.find((f) => f.name === selectedFieldName);
                          const fieldType = selectedField?.type;

                          return (
                            <div key={index} className={styles.joinConditionGroup}>
                              {index > 0 && <OperatorToggle name={`joinConditions.${index}.logicalOperator`} />}

                              <div
                                className={`${styles.joinConditionFieldsWrapper} ${
                                  highlightedField && condition.sourceField === highlightedField
                                    ? styles.highlightedCondition
                                    : ''
                                }`}
                              >
                                <Field
                                  name={`joinConditions.${index}.sourceField`}
                                  className={styles.joinConditionField}
                                  component={BngField}
                                  inputComponent={BngSelectSearch}
                                  required={true}
                                  withLabel={false}
                                  options={currentNodeFields.map((field) => ({
                                    value: field.name,
                                    label: field.name,
                                  }))}
                                  onChange={(value) => {
                                    setFieldValue(`joinConditions.${index}.sourceField`, value);
                                    const selectedField = currentNodeFields?.find((f) => f.name === value);
                                    if (selectedField?.type === 'BOOL') {
                                      setFieldValue(`joinConditions.${index}.operator`, '=');
                                    } else {
                                      setFieldValue(`joinConditions.${index}.operator`, '');
                                    }
                                  }}
                                />
                                <Field
                                  name={`joinConditions.${index}.operator`}
                                  className={styles.joinConditionField}
                                  component={BngField}
                                  inputComponent={BngSelectSearch}
                                  required={true}
                                  withLabel={false}
                                  disabled={fieldType === 'BOOL'}
                                  options={
                                    fieldType === 'NUMERIC' || fieldType === 'DATE'
                                      ? comparisonOptions.numeric
                                      : fieldType === 'TEXT'
                                      ? comparisonOptions.text
                                      : fieldType === 'BOOL'
                                      ? comparisonOptions.bool
                                      : []
                                  }
                                />
                                <Field
                                  name={`joinConditions.${index}.targetField`}
                                  className={styles.joinConditionField}
                                  component={BngField}
                                  inputComponent={BngSelectSearch}
                                  required={true}
                                  withLabel={false}
                                  options={getTargetFieldOptions(index, condition.targetField)}
                                  onSearch={({ search }) => {
                                    setTargetFieldSearchTerms((prev) => ({
                                      ...prev,
                                      [index]: search,
                                    }));
                                  }}
                                  onChange={(value) => {
                                    let finalValue = value;
                                    if (typeof value === 'string' && value.startsWith('__FIXED_VALUE__')) {
                                      finalValue = value.replace('__FIXED_VALUE__', '');
                                      setTargetFieldSearchTerms((prev) => ({
                                        ...prev,
                                        [index]: '',
                                      }));
                                    } else {
                                      const targetFieldInfo = otherNodesFields.find(
                                        (field) => field.displayName === value
                                      );
                                      if (targetFieldInfo) {
                                        setFieldValue(`joinConditions.${index}.targetNodeId`, targetFieldInfo.nodeId);
                                      }
                                    }

                                    setFieldValue(`joinConditions.${index}.targetField`, finalValue);
                                  }}
                                />

                                <BngIconButton
                                  icon={'close'}
                                  onClick={() => {
                                    remove(index);
                                  }}
                                  className={styles.removeButton}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </FieldArray>
                </Dialog.Body>

                <Dialog.Footer className={styles.dialogFooter}>
                  <BngIconButton
                    icon={'delete'}
                    text={t('delete.table')}
                    className={styles.deleteTableButton}
                    onClick={() => {
                      onDeleteTable(nodeId);
                      closeModal();
                    }}
                  />
                  <DefaultDialogActions
                    closeModal={closeModal}
                    submitCount={submitCount}
                    okLabel="apply"
                    cancelLabel="cancel"
                    disabled={values.joinConditions.length === 0}
                    hideCancelButton={shouldHideCloseAndCancel}
                  />
                </Dialog.Footer>
              </BngForm>
            </UiBlocker>
          </Dialog>
        );
      }}
    </Formik>
  );
}
